import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  importChain,
  importIdentifiers,
  searchChains,
  type NetworkChainSearchResult,
  type NetworkImportResult,
} from "@services/networkManagement";
import { DASHBOARD } from "../dashboard/dashboardTheme";

export type NetworkDefineModalProps = {
  open: boolean;
  networkId: number | null;
  onClose: () => void;
  onImported: (result: NetworkImportResult) => void;
};

type IdentifierType = "npi" | "ccn";

export default function NetworkDefineModal({
  open,
  networkId,
  onClose,
  onImported,
}: NetworkDefineModalProps) {
  const [tab, setTab] = useState(0);
  const [chainQuery, setChainQuery] = useState("");
  const [chainResults, setChainResults] = useState<NetworkChainSearchResult[]>([]);
  const [selectedChainId, setSelectedChainId] = useState<string | null>(null);
  const [chainLoading, setChainLoading] = useState(false);
  const [identifierType, setIdentifierType] = useState<IdentifierType>("npi");
  const [identifierText, setIdentifierText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setTab(0);
      setChainQuery("");
      setChainResults([]);
      setSelectedChainId(null);
      setIdentifierText("");
      setError(null);
    }
  }, [open]);

  const runChainSearch = useCallback(async () => {
    if (!chainQuery.trim()) return;
    setChainLoading(true);
    setError(null);
    try {
      const rows = await searchChains(chainQuery.trim());
      setChainResults(rows);
      setSelectedChainId(rows[0]?.chain_id ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Chain search failed");
    } finally {
      setChainLoading(false);
    }
  }, [chainQuery]);

  useEffect(() => {
    if (tab !== 0 || !chainQuery.trim()) return;
    const t = window.setTimeout(() => void runChainSearch(), 400);
    return () => window.clearTimeout(t);
  }, [chainQuery, tab, runChainSearch]);

  const parseIdentifiers = () =>
    identifierText
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);

  const handleImport = async () => {
    if (networkId == null) {
      setError("Select or create a network first.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      let result: NetworkImportResult;
      if (tab === 0) {
        if (!selectedChainId) throw new Error("Select a chain to import");
        result = await importChain(networkId, selectedChainId);
      } else {
        const ids = parseIdentifiers();
        if (!ids.length) throw new Error("Enter at least one identifier");
        result = await importIdentifiers(networkId, identifierType, ids);
      }
      onImported(result);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Import failed");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedChain = chainResults.find((c) => c.chain_id === selectedChainId);
  const idCount = tab === 1 ? parseIdentifiers().length : selectedChain?.facility_count ?? 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Define your network
        <Typography sx={{ fontSize: 12.5, color: DASHBOARD.textMuted, mt: 0.5 }}>
          Import by chain name or NPI/CCN list — we pull CMS public data for every facility.
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="Search by chain / network name" sx={{ textTransform: "none" }} />
          <Tab label="Import identifier list" sx={{ textTransform: "none" }} />
        </Tabs>

        {tab === 0 ? (
          <Box>
            <Box sx={{ display: "flex", gap: 1, mb: 1.5, alignItems: "center" }}>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Cascade Care Group, Sunrise Health…"
                value={chainQuery}
                onChange={(e) => setChainQuery(e.target.value)}
              />
              <Button
                variant="contained"
                size="small"
                onClick={() => void runChainSearch()}
                sx={{ textTransform: "none", whiteSpace: "nowrap", flexShrink: 0 }}
              >
                Search CMS
              </Button>
            </Box>
            <Typography sx={{ fontSize: 12, color: DASHBOARD.textMuted, mb: 1.5 }}>
              Searches the CMS Care Compare chain affiliation registry.
            </Typography>
            {chainLoading ? <CircularProgress size={24} /> : null}
            <Box sx={{ border: `1px solid ${DASHBOARD.border}`, borderRadius: "8px", maxHeight: 220, overflowY: "auto" }}>
              {chainResults.map((row) => {
                const selected = row.chain_id === selectedChainId;
                return (
                  <Box
                    key={row.chain_id}
                    onClick={() => setSelectedChainId(row.chain_id)}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr auto auto",
                      gap: 1.5,
                      alignItems: "center",
                      p: 1.5,
                      cursor: "pointer",
                      bgcolor: selected ? DASHBOARD.iconButtonBg : "#fff",
                      borderBottom: `1px solid ${DASHBOARD.borderSubtle}`,
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{row.chain_name}</Typography>
                      <Typography sx={{ fontSize: 11.5, color: DASHBOARD.textMuted }}>
                        {row.states.join(" · ")} · Chain ID {row.chain_id}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 11, bgcolor: "#eef3f6", px: 1, borderRadius: 10 }}>
                      {row.facility_count} facilities
                    </Typography>
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        border: `1.5px solid ${selected ? DASHBOARD.accent : DASHBOARD.border}`,
                        bgcolor: selected ? DASHBOARD.accent : "transparent",
                        color: "#fff",
                        fontSize: 10,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {selected ? "✓" : ""}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ) : (
          <Box>
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              {(["npi", "ccn"] as IdentifierType[]).map((t) => (
                <Button
                  key={t}
                  variant={identifierType === t ? "contained" : "outlined"}
                  onClick={() => setIdentifierType(t)}
                  sx={{ textTransform: "none" }}
                >
                  {t.toUpperCase()}
                </Button>
              ))}
            </Box>
            <Typography sx={{ fontSize: 12, color: DASHBOARD.textMuted, mb: 1 }}>
              {identifierType === "npi"
                ? "One NPI per line"
                : "One CMS Certification Number (CCN) per line"}
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={5}
              placeholder={identifierType === "npi" ? "1234567890\n1234567891" : "015009\n015010"}
              value={identifierText}
              onChange={(e) => setIdentifierText(e.target.value)}
              sx={{ fontFamily: "monospace", fontSize: 12.5 }}
            />
          </Box>
        )}

        {error ? (
          <Typography sx={{ color: BAD, mt: 2, fontSize: 13 }}>{error}</Typography>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: 11.5, color: DASHBOARD.textMuted }}>
          CMS Care Compare · CASPER · PBJ · SNF VBP
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={onClose} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={submitting || networkId == null || idCount === 0}
            onClick={() => void handleImport()}
            sx={{ textTransform: "none" }}
          >
            {submitting ? <CircularProgress size={18} color="inherit" /> : `Import ${idCount} facilities →`}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

const BAD = "#b85c5c";
